import { CustomFieldResourceType } from "@repo/app-types/entities/customField";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { IconExternalLink } from "@tabler/icons-react";
import type { FC } from "react";
import { Button } from "react-aria-components";
import { useAllDataExtractionFieldDefinitionsQuery } from "../../api/entities/dataExtractionField.js";
import { appPaths } from "../../app/appPaths.js";

interface DataExtractionFieldOption {
  id: string;
  name: string;
}

interface TeammateAgentDataExtractionFieldsSectionProps {
  playbookId: PlaybookId | null;
  fieldIds: readonly string[];
  onChange: (fieldIds: string[]) => void;
  isDisabled?: boolean;
  hasUnpublishedChanges?: boolean;
}

export const TeammateAgentDataExtractionFieldsSection: FC<
  TeammateAgentDataExtractionFieldsSectionProps
> = ({ playbookId, fieldIds, onChange, isDisabled, hasUnpublishedChanges }) => {
  const {
    data: allDataExtractionFieldDefinitions,
    isFetching: isLoadingFields,
  } = useAllDataExtractionFieldDefinitionsQuery();

  // Filter to only grading resource type fields
  const allGradingFieldDefinitions =
    allDataExtractionFieldDefinitions?.filter(
      (def) => def.resource_type === CustomFieldResourceType.GRADING,
    ) ?? [];

  const fieldOptions: DataExtractionFieldOption[] =
    allGradingFieldDefinitions.map((def) => ({
      id: def.id,
      name: def.display_name,
    }));

  // Ids of fields that no longer exist would have no chip to render, so drop
  // them the way the previous option-object mapping did.
  const selectedFieldIds = fieldIds.filter((id) =>
    fieldOptions.some((option) => option.id === id),
  );

  return (
    <div className="flex w-full items-center gap-2">
      <div className="w-full">
        <Label size={Size.SMALL} className="mb-2">
          <span className="flex items-center gap-1.5">
            Data extraction
            {hasUnpublishedChanges && (
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            )}
          </span>
        </Label>
        {allGradingFieldDefinitions.length === 0 ? (
          <div className="text-sm text-gray-600">
            Configure{" "}
            <Button
              className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
              onClick={() => {
                window.open(appPaths.settingsDataExtraction.build(), "_blank");
              }}
            >
              data extraction{" "}
              <IconExternalLink className="mb-1 inline size-4 align-middle" />
            </Button>{" "}
            to automatically extract information from customer interactions.
          </div>
        ) : (
          <>
            <div className="w-full">
              <ComboboxField<DataExtractionFieldOption, "multiple">
                aria-label="Data extraction fields"
                selectionMode="multiple"
                items={fieldOptions}
                getLabel={(option) => option.name}
                value={selectedFieldIds}
                onChange={(keys) => {
                  onChange(keys.map(String));
                }}
                placeholder={
                  isLoadingFields
                    ? "Loading fields..."
                    : "Select data extraction fields"
                }
                chipsLabel="Selected data extraction fields"
                isDisabled={isDisabled || isLoadingFields || !playbookId}
              />
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Select fields to extract from customer interactions. Configure
              fields from your{" "}
              <span
                className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                onClick={() => {
                  window.open(
                    appPaths.settingsDataExtraction.build(),
                    "_blank",
                  );
                }}
              >
                data extraction settings
              </span>
              .
            </div>
          </>
        )}
      </div>
    </div>
  );
};
